import React, { useEffect, useState } from "react";
import { Table } from "reactstrap";
import Moment from "react-moment";
import moment from "moment";
import classNames from "classnames";
import { FileInfo } from "../Home/HomeStyled";
import { TextLabel } from "../../Styled";
import { connect } from "react-redux";
import { TableLoader } from "../../Loader/index";
import { getTableColumnKeys, getObsoleteAlternateCount, getUnmatchedViewOptionsCache, hasLineAlternateSelected, hasObsoleteAlternateSelected, isBomSectionRow, isLeadlessLine, isObsoleteLine, isQuoteSkippedLine, isUnmatchedLine, LEADLESS_HIGHLIGHT_ARGB, LEADLESS_ROW_CLASS, normalizeExcelHeaderKey, OBSOLETE_HIGHLIGHT_ARGB, OBSOLETE_RESOLVED_ROW_CLASS, OBSOLETE_ROW_CLASS, resolveLineSelectedAlternateRecommendation, UNMATCHED_EXACT_MATCH_MESSAGE, UNMATCHED_RESOLVED_ROW_CLASS, UNMATCHED_ROW_CLASS, UNMATCHED_VIEW_OPTIONS_LABEL } from "../../../constants/digiKeyColumns";
import { SelectedAlternateMpnRow } from "../../AlternateRecommendationBadge";
import "./BomPageBomDetailsStyle.css";

const getClassNameForColor = (argb) => {
  switch (argb) {
    case "effbf8":
    case "04b78a":
      return "bg-green";
    case "0080ff":
      return "bg-blue";
    case "ffedad":
      return "bg-yellow";
    case "fdeeee":
      return "bg-light-maroon";
    case "e9e9fc":
      return "bg-gray-opacity";
    case LEADLESS_HIGHLIGHT_ARGB:
      return LEADLESS_ROW_CLASS;
    case OBSOLETE_HIGHLIGHT_ARGB:
      return OBSOLETE_ROW_CLASS;
    default:
      return "";
  }
};

const getClassNameForResultObject = (font) => {
  if (font?.color?.argb === "4b499f") {
    return "tdTitle";
  } else if (font?.color?.argb === "636e95") {
    return "tdpartsDetailsKey";
  } else if (font?.italic) {
    return "tdItalicSubTitle";
  }
  return "";
};

const renderStructuredCell = (value) => {
  if (value != null && typeof value === "object" && value.description != null) {
    return value.description.map((entry, index) => (
      <React.Fragment key={index}>
        {entry.link && !entry.key ? (
          <a href={entry.link} target="_blank" rel="noopener noreferrer">
            {entry.linkText || "Datasheet"}
          </a>
        ) : (
          <span className="tdpartsDetailsEntry">
            <span className="tdpartsDetailsKey">{entry.key + ": "}</span>
            <span className="tdpartsDetailsValue">
              {entry.link ? (
                <a href={entry.link} target="_blank" rel="noopener noreferrer">
                  {entry.linkText}
                </a>
              ) : entry.value ? (
                entry.value
              ) : (
                "n/a"
              )}
            </span>
          </span>
        )}
        <br />
      </React.Fragment>
    ));
  }

  if (value != null && typeof value === "object" && value.part_status != null) {
    return value.part_status.map((entry, index) => (
      <React.Fragment key={index}>
        <span className="tdpartsDetailsEntry">
          <span className="tdpartsDetailsKey">{entry.key + ": "}</span>
          <span className="tdpartsDetailsValue">
            {entry.value ? entry.value : "n/a"}
          </span>
        </span>
        <br />
      </React.Fragment>
    ));
  }

  if (value != null && typeof value === "object") {
    return value.toString();
  }

  return value != null ? value.toString() : "";
};

const renderUnmatchedViewOptionsAction = (row, onSelectUnmatched, idleLabel = "") => {
  const count = getUnmatchedViewOptionsCache(row).length;
  const loaded = row.substitutes_loaded === true || count > 0;
  const label =
    idleLabel ||
    (loaded || count > 0
      ? `${UNMATCHED_VIEW_OPTIONS_LABEL} (${count})`
      : UNMATCHED_VIEW_OPTIONS_LABEL);

  if (typeof onSelectUnmatched !== "function") {
    return <small className="text-muted">{label}</small>;
  }

  return (
    <button
      type="button"
      className="unmatched-select-link"
      onClick={(e) => {
        e.stopPropagation();
        onSelectUnmatched(row);
      }}
    >
      {label}
    </button>
  );
};

const renderManufacturerPartNumberCell = (row, col, handlers = {}) => {
  const { onSelectObsolete, onSelectUnmatched } = handlers;
  const selectedRecommendation = resolveLineSelectedAlternateRecommendation(row);

  if (isUnmatchedLine(row)) {
    const mpn = row.unmatched_original_mpn || row.manufacturer_part_number;

    if (hasLineAlternateSelected(row)) {
      return (
        <div className="unmatched-mpn-cell">
          <SelectedAlternateMpnRow
            mpn={row.selected_alternate_mpn}
            mpnClassName="unmatched-selected-mpn"
            label={selectedRecommendation.label}
            text={selectedRecommendation.text}
          />
          <small className="text-muted unmatched-replaces-label obsolete-mpn-meta">
            Unmatched: {mpn}
          </small>
          <div className="obsolete-mpn-action">
            {renderUnmatchedViewOptionsAction(
              row,
              onSelectUnmatched,
              "Change suggested part"
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="unmatched-mpn-cell">
        <span>{mpn}</span>
        <br />
        <span className="text-muted small">
          {UNMATCHED_EXACT_MATCH_MESSAGE}{" "}
          {renderUnmatchedViewOptionsAction(row, onSelectUnmatched)}
        </span>
      </div>
    );
  }

  if (!isObsoleteLine(row)) {
    return renderStructuredCell(row[col]);
  }

  const alternateCount = getObsoleteAlternateCount(row);

  return (
    <div className="obsolete-mpn-cell">
      {hasObsoleteAlternateSelected(row) ? (
        <>
          <SelectedAlternateMpnRow
            mpn={row.selected_alternate_mpn}
            label={selectedRecommendation.label}
            text={selectedRecommendation.text}
          />
          <small className="text-muted obsolete-replaces-label obsolete-mpn-meta">
            Obsolete: {row.obsolete_part_number || row.manufacturer_part_number}
          </small>
        </>
      ) : isQuoteSkippedLine(row) ? (
        <>
          <span>{row.obsolete_part_number || row.manufacturer_part_number}</span>
          <small className="d-block text-muted obsolete-mpn-meta">
            Skipped — will not be quoted
          </small>
        </>
      ) : (
        <span>{row.obsolete_part_number || row.manufacturer_part_number}</span>
      )}
      {typeof onSelectObsolete === "function" ? (
        <div className="obsolete-mpn-action">
          <button
            type="button"
            className="obsolete-select-link"
            onClick={(e) => {
              e.stopPropagation();
              onSelectObsolete(row);
            }}
          >
            {alternateCount > 0
              ? `Alternates: ${alternateCount} – click to select`
              : "Click to select"}
          </button>
        </div>
      ) : alternateCount > 0 ? (
        <small className="text-muted obsolete-mpn-action">
          Alternates: {alternateCount} available
        </small>
      ) : null}
    </div>
  );
};

const renderBomCell = (row, col, handlers) => {
  const normalizedCol = normalizeExcelHeaderKey(col);

  if (normalizedCol === "manufacturer_part_number") {
    return renderManufacturerPartNumberCell(row, col, handlers);
  }

  if (
    normalizedCol === "manufacturer" &&
    (isObsoleteLine(row) || isUnmatchedLine(row)) &&
    hasLineAlternateSelected(row)
  ) {
    return (
      row.selected_alternate_manufacturer ||
      renderStructuredCell(row[col])
    );
  }

  return renderStructuredCell(row[col]);
};

const BomPageBomDetails = (props) => {
  const [bomMetadata, setBomMetadata] = useState({});
  const [bomLines, setBomLines] = useState();

  useEffect(() => {
    if (props?.bomDetails?.data) {
      setBomMetadata(props.bomDetails?.data);
    }
    if (props?.bomLines) {
      setBomLines(props.bomLines);
    }
  }, [props]);

  const structuredBomlines = props.structuredBomlines;
  const tableLoading = props.tableLoading;
  const cellHandlers = {
    onSelectObsolete: props.onSelectObsolete,
    onSelectUnmatched: props.onSelectUnmatched,
  };
  const columnKeys =
    structuredBomlines && structuredBomlines.length
      ? getTableColumnKeys(structuredBomlines)
      : [];

  return (
    <div className="bomPageBomDetails">
      <div className="importBlock ">
        <div className="importBlock radius-8 bg-white">
          <FileInfo>
            <TextLabel>Customer Name</TextLabel>
            <h5>{bomMetadata.customer_name ?? "..."}</h5>
          </FileInfo>
          <FileInfo>
            <TextLabel>Date</TextLabel>
            <h5>
              {bomMetadata.date_of_upload ? (
                <Moment format="DD MMM, YYYY">
                  {moment(bomMetadata.date_of_upload, "DD/MM/YYYY").toDate()}
                </Moment>
              ) : (
                "..."
              )}
            </h5>
          </FileInfo>
          <FileInfo>
            <TextLabel>Part #REV</TextLabel>
            <h5>{bomMetadata.part_rev ?? "..."}</h5>
          </FileInfo>
          <FileInfo>
            <TextLabel>BOM Quote ID</TextLabel>
            <h5>{bomMetadata.id ?? "..."}</h5>
          </FileInfo>
          <FileInfo>
            <TextLabel>Created By</TextLabel>
            <h5>{bomMetadata.created_by ?? "..."}</h5>
          </FileInfo>
          <div className="table-responsive mh-430">
            {tableLoading ? (
              <TableLoader />
            ) : structuredBomlines && structuredBomlines.length > 0 ? (
              <Table className="table-bordered table-gray hovered mb-0 table-sticky-header">
                <thead>
                  <tr>
                    {columnKeys.map((col, index) => (
                      <th
                        key={index}
                        className={classNames({
                          "bg-blue": col.indexOf("Quote ") !== -1,
                        })}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {structuredBomlines.map((row, rowIndex) => {
                    if (isBomSectionRow(row)) {
                      return (
                        <tr key={rowIndex} className="bom-section-row">
                          <td
                            colSpan={columnKeys.length}
                            className="bg-secondary text-white font-weight-bold"
                          >
                            <span className="bom-section-title">
                              {row._section_title ||
                                row.manufacturer_part_number ||
                                ""}
                            </span>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr
                        key={rowIndex}
                        className={classNames({
                          [LEADLESS_ROW_CLASS]: isLeadlessLine(row),
                          [OBSOLETE_ROW_CLASS]:
                            isObsoleteLine(row) &&
                            !hasLineAlternateSelected(row),
                          [OBSOLETE_RESOLVED_ROW_CLASS]:
                            isObsoleteLine(row) &&
                            hasLineAlternateSelected(row),
                          [UNMATCHED_ROW_CLASS]:
                            isUnmatchedLine(row) &&
                            !hasLineAlternateSelected(row),
                          [UNMATCHED_RESOLVED_ROW_CLASS]:
                            isUnmatchedLine(row) &&
                            hasLineAlternateSelected(row),
                        })}
                        title={
                          isObsoleteLine(row) && row.obsolete_warning
                            ? row.obsolete_warning
                            : isUnmatchedLine(row) && row.unmatched_warning
                            ? row.unmatched_warning
                            : undefined
                        }
                      >
                        {columnKeys.map((col, colIndex) => (
                          <td
                            key={colIndex}
                            className={classNames({
                              "bg-blue": col.indexOf("Quote ") !== -1,
                            })}
                          >
                            {renderBomCell(row, col, cellHandlers)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : bomLines && bomLines.length > 0 ? (
              <Table className="table-bordered table-gray hovered mb-0 table-sticky-header">
                <thead>
                  <tr>
                    {Object.values(bomLines[0]._cells).map((item, index) => {
                      return (
                        <th
                          key={index}
                          className={getClassNameForColor(
                            item?.style?.fill?.fgColor?.argb
                          )}
                        >
                          {item._value.model.value}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="">
                  {/* eslint-disable-next-line array-callback-return */}
                  {bomLines.map((item, index) => {
                    if (index !== 0) {
                      return (
                        <tr key={index}>
                          {Object.values(item._cells).map((cell, index) => {
                            let cellValue = cell._value.model.value;
                            return (
                              <td
                                key={index}
                                className={getClassNameForColor(
                                  cell?.style?.fill?.fgColor?.argb
                                )}
                              >
                                {typeof cellValue === "object" &&
                                cellValue.richText
                                  ? Object.values(cellValue.richText).map(
                                      (item, index) => {
                                        return (
                                          <React.Fragment>
                                            <span
                                              key={index}
                                              className={getClassNameForResultObject(
                                                item.font
                                              )}
                                            >
                                              {item.text}
                                            </span>
                                            {item.text.includes("\n") ? (
                                              <br />
                                            ) : (
                                              ""
                                            )}
                                          </React.Fragment>
                                        );
                                      }
                                    )
                                  : typeof cellValue === "object"
                                  ? cellValue.toString()
                                  : cell?._value?.model?.value?.toString()}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </Table>
            ) : (
              <TableLoader />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps, {})(BomPageBomDetails);
